import {
  ColumnLayout,
  Box,
  Button,
} from "@cloudscape-design/components-themed";
import { Dispatch, SetStateAction } from "react";
import { useNavigate } from "react-router-dom";
import { useCanIShowHook } from "../../app/useCanIShowHook";
import { useHasAuthorityHook } from "../../app/useHasAuthorityHook";
import { USER_TYPE, PERMISSIONS } from "../../configs/constants";
import { COMMON_LABELS } from "../default-constants";
import { IErrMsg, LABELS, UPDATING_INFO } from "./constants";
import { isEmpty, isEqual, isUndefined } from "lodash";
import {
  BankAccountsUpdateRequest,
  SettlementNoticeEmail,
} from "../../features/api-interfaces";
import { showModal } from "../UI/modal/modal-slice";
import { useAppDispatch } from "../../app/hook";
import { usePutBankAccountsUpdateMutation } from "../../features/api";
import { addNotification } from "../../features/error/notifications-slice";
import { validRequest } from "./validation";

interface IFooter {
  isFetching: boolean;
  companyContractBankAccountNo: number;
  updatingInfo?: string;
  applyingDate?: string;
  isUpdate: boolean;
  setIsUpdate: Dispatch<SetStateAction<boolean>>;
  editingRequest: BankAccountsUpdateRequest;
  settlementNoticeEmailList: SettlementNoticeEmail[];
  setErrMsg: Dispatch<SetStateAction<IErrMsg>>;
}

/**
 * 入金口座情報照会・更新 フッター
 */
export const Footer = (props: IFooter) => {
  const {
    isFetching,
    companyContractBankAccountNo,
    updatingInfo,
    applyingDate,
    isUpdate,
    setIsUpdate,
    editingRequest,
    settlementNoticeEmailList,
    setErrMsg,
  } = props;
  const navigator = useNavigate();
  const dispatch = useAppDispatch();
  const isDocomoUser = useCanIShowHook(USER_TYPE.DOCOMO);
  const hasAuthority = useHasAuthorityHook(PERMISSIONS.APPLY_COMPANY_EDIT);
  const [putBankAccountsUpdate] = usePutBankAccountsUpdateMutation();
  const isUpdateDisabled =
    !isFetching &&
    (!isEqual(updatingInfo, UPDATING_INFO.NONE) || !isEmpty(applyingDate));

  /**
   * 戻るボタンを押下したときの処理.
   */
  const onBack = () => {
    if (isUpdate) {
      navigator(`/bank-accounts/${companyContractBankAccountNo}`);
    } else {
      navigator("/bank-accounts");
    }
    setIsUpdate(false);
  };

  /**
   * 口座情報変更ボタンを押下したときの処理.
   */
  const onUpdateBankAccount = () => {
    navigator(`/bank-accounts/${companyContractBankAccountNo}/update`);
  };

  /**
   * 更新ボタンを押下したときの処理.
   */
  const onUpdate = () => {
    dispatch(
      showModal({
        content: COMMON_LABELS.CONTENTS.REGISTER_CONFIRM,
        positiveFn: () => {
          const request: BankAccountsUpdateRequest = {
            ...editingRequest,
            settlementNoticeEmailList,
          };

          const errMsg: IErrMsg = validRequest(request);
          if (!Object.values(errMsg).every((value) => isUndefined(value))) {
            // いずれかが入力エラーであればリクエストは送らない
            setErrMsg(errMsg);
            return;
          }

          putBankAccountsUpdate(request).then((res: any) => {
            if ("error" in res && "data" in res.error) {
              const { messageId, errorDetail } = res.error.data;
              if (messageId) {
                dispatch(
                  addNotification({
                    msgId: messageId,
                    msgArgs: errorDetail ?? [],
                  })
                );
              }
            } else {
              navigator(`/bank-accounts/${companyContractBankAccountNo}`);
            }
          });
        },
      })
    );
  };

  return (
    <ColumnLayout columns={2}>
      <Box float={"left"}>
        <Button formAction={"none"} variant={"link"} onClick={onBack}>
          {COMMON_LABELS.BTNS.BACK}
        </Button>
      </Box>
      {isDocomoUser && hasAuthority && (
        <Box float={"right"} margin={"l"}>
          {isUpdate ? (
            <Button
              variant={"primary"}
              onClick={onUpdate}
              disabled={isUpdateDisabled}
            >
              {COMMON_LABELS.BTNS.UPDATE}
            </Button>
          ) : (
            <Button
              variant={"normal"}
              onClick={onUpdateBankAccount}
              disabled={isUpdateDisabled}
            >
              {LABELS.UPDATE_BANK_ACCOUNT}
            </Button>
          )}
        </Box>
      )}
    </ColumnLayout>
  );
};
